import axios, { AxiosRequestConfig, Method, CanceledError } from "axios";

import { __API_BASE_URL__ } from "..";
import { getRefreshToken } from "../../lib/getRefreshToken";

import { clearAuthSession } from "@/entities/localstorage/model/lib/authSessionStorage";
import { refreshToken } from "@/entities/session/api/refreshToken";
import i18n from "@/shared/config/i18n";
import { showToast } from "@/shared/lib/showToast";

interface RequestConfig extends Omit<AxiosRequestConfig, "url" | "method" | "data"> {
    body?: any;
    fullResponse?: boolean;
    isPublic?: boolean;
}

let isRefreshing = false;
let failedQueue: { resolve: (value?: any) => void; reject: (reason?: any) => void }[] = [];
let controller = new AbortController();
const ACCESS_TOKEN_KEY = "accessToken";
let accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY) || "";
const MAX_REFRESH_ATTEMPTS = 5;
let refreshAttempts = 0;

export const getAccessToken = () => accessToken;
export const setAccessToken = (token: string) => {
    accessToken = token;
    if (token) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    }
};

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

const makeRequest = async (
    method: Method | "UPDATE",
    path: string,
    config?: RequestConfig,
    header: boolean = false,
    authToken: boolean = false,
): Promise<any> => {
    const { body, headers: customHeaders, fullResponse, isPublic, ...restConfig } = config || {};
    const token = getAccessToken();

    const headers = {
        "Content-Type": !header ? "application/json" : "multipart/form-data",
        "Cache-Control": "no-cache",
        Authorization: token,
        ...customHeaders,
    };

    const headerWithOutAuth = {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        ...customHeaders,
    };

    try {
        const response = await axios({
            method: method,
            url: path.startsWith("http") ? path : `${__API_BASE_URL__}${path}`,
            data: body,
            headers: authToken || isPublic ? headerWithOutAuth : headers,
            signal: isPublic ? undefined : controller.signal,
            ...restConfig,
        });
        if (fullResponse) {
            return response;
        }
        return response.data;
    } catch (e: any) {
        if (isPublic) throw e;

        // обрабатываем запросы, отмененные AbortController'ом
        if (e instanceof CanceledError) {
            // ставим отмененные из-за рефреша токена запросы в очередь
            // для повторного выполнения после рефреша
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(() => makeRequest(method, path, config, header, authToken));
        }

        const codeStatus = e.response?.status;
        const requestURL = e.request?.responseURL;

        if (codeStatus === 401) {
            const noRetry = ["captcha", "refresh_token"];
            if (noRetry.some((keyword) => requestURL?.includes(keyword))) {
                throw e;
            }

            if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
                console.error(
                    `[Auth] Token refresh failed after ${MAX_REFRESH_ATTEMPTS} attempts. Redirecting to login.`,
                );
                processQueue(new Error("Max refresh attempts reached"), null);
                showToast("error", i18n.t("common.toasts.session-expired-login"));

                refreshAttempts = 0;
                isRefreshing = false;
                setAccessToken("");
                clearAuthSession();
                localStorage.removeItem("refreshToken");

                window.location.href = "/login";

                return Promise.reject(new Error("Session expired"));
            }

            // Если обновление уже выполняется, ставим этот запрос в очередь
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => makeRequest(method, path, config, header, authToken));
            }

            isRefreshing = true;
            refreshAttempts += 1;
            console.warn(
                `[Auth] Access token expired. Refresh attempt ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS}. Triggered by: ${requestURL}`,
            );

            // отменяем все текущие запросы
            controller.abort();
            // создаем новый контроллер для будущих запросов
            controller = new AbortController();

            const refreshTokenValue = getRefreshToken();
            if (!refreshTokenValue) {
                isRefreshing = false;
                clearAuthSession();
                window.location.href = "/login";
                return Promise.reject(new Error("No refresh token available"));
            }

            try {
                await refreshToken();
                refreshAttempts = 0;
                processQueue(null, getAccessToken());
                return makeRequest(method, path, config, header, authToken);
            } catch (err: any) {
                const status = err?.response?.status;
                const message = err?.message;
                console.error(
                    `[Auth] Refresh token request failed. Status: ${status ?? "N/A"}, Error: ${message}`,
                );
                const error = err as Error;
                processQueue(error, null);
                showToast("error", i18n.t("common.toasts.session-expired-refresh"));
                return Promise.reject(error);
            } finally {
                isRefreshing = false;
            }
        }

        console.error(`Серверная ошибка: ${e.message}`);
        throw e;
    }
};

export default makeRequest;
